import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

type Language = "en" | "fr" | "ar" | "it";

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations: Record<Language, Record<string, string>> = {
  en: {
    // Navigation
    "nav.services": "Services",
    "nav.platform": "Platform",
    "nav.getStarted": "Get Started",
    "nav.signIn": "Sign In",
    "nav.myPortal": "My Portal",
    
    // Landing Page - Hero
    "landing.hero.partner": "Your Marketing Partner",
    "landing.hero.title": "AI-Powered Marketing Agency in Morocco",
    "landing.hero.description": "Born in Martil, Morocco in 2019. Reborn with AI in 2025. We craft cutting-edge AI SaaS solutions and intelligent marketing experiences that blend creativity with intelligent technology for global brands.",
    "landing.hero.requestService": "Request Service",
    "landing.hero.viewRequests": "View My Requests",
    
    // Stats
    "landing.stats.clients": "Happy Clients",
    "landing.stats.projects": "Projects Completed",
    "landing.stats.satisfaction": "Client Satisfaction",
    "landing.stats.support": "Support Available",
    
    // Features
    "landing.features.badge": "Our Expertise",
    "landing.features.title": "AI-Enhanced Marketing Solutions",
    "landing.features.subtitle": "Combining human creativity with AI intelligence for exceptional results",
    "landing.features.strategic": "Strategic Planning",
    "landing.features.strategicDesc": "Data-driven strategies tailored to your business goals",
    "landing.features.fast": "Fast Turnaround",
    "landing.features.fastDesc": "Quick execution without compromising on quality",
    "landing.features.growth": "Growth Focused",
    "landing.features.growthDesc": "Proven methods to scale your business",
    "landing.features.support": "Dedicated Support",
    "landing.features.supportDesc": "A team committed to your success",
    
    // Platform
    "landing.platform.badge": "Our Platform",
    "landing.platform.title": "Intelligent Project Management",
    "landing.platform.subtitle": "Experience seamless collaboration with our AI-powered platform designed for modern agencies",
    "landing.platform.dashboard": "Dashboard Overview",
    "landing.platform.dashboardDesc": "Get a comprehensive view of all your active projects, recent activities, and key metrics at a glance.",
    "landing.platform.projects": "Project Management",
    "landing.platform.projectsDesc": "Track project progress, view milestones, monitor budgets, and manage all deliverables in one place.",
    "landing.platform.requests": "Service Requests",
    "landing.platform.requestsDesc": "View and track all your service requests with real-time status updates and direct communication.",
    "landing.platform.newRequest": "Submit New Requests",
    "landing.platform.newRequestDesc": "Easily submit new service requests with detailed descriptions and automatic notifications to our team.",
    "landing.platform.invoices": "Invoices & Payments",
    "landing.platform.invoicesDesc": "Access all your invoices, payment history, and transaction details with downloadable PDF copies.",
    "landing.platform.chat": "Direct Communication",
    "landing.platform.chatDesc": "Chat directly with our team, receive instant notifications, and get support whenever you need it.",
    "landing.platform.analytics": "Analytics & Insights",
    "landing.platform.analyticsDesc": "Monitor project performance, review detailed analytics, and track your marketing ROI with comprehensive reports.",
    
    // Form
    "landing.form.title": "Request a Service",
    "landing.form.subtitle": "Fill out the form below and our team will get back to you within 24 hours",
    "landing.form.serviceType": "Service Type",
    "landing.form.selectService": "Select a service",
    "landing.form.businessName": "Business Name",
    "landing.form.contactName": "Contact Name",
    "landing.form.email": "Email Address",
    "landing.form.phone": "Phone Number",
    "landing.form.description": "Project Description",
    "landing.form.descPlaceholder": "Tell us about your project and goals...",
    "landing.form.budget": "Budget Range",
    "landing.form.selectBudget": "Select budget range",
    "landing.form.startDate": "Preferred Start Date",
    "landing.form.submit": "Submit Request",
    "landing.form.submitting": "Submitting...",
    
    // Common
    "common.quickSetup": "Quick Setup",
    "common.noCreditCard": "No Credit Card Required",
    "common.realTimeUpdates": "Real-Time Updates",
    "common.fileManagement": "File Management",
    "common.directMessaging": "Direct Messaging",
    "common.meetingScheduler": "Meeting Scheduler",
    "common.performanceMetrics": "Performance Metrics",
    "common.roiTracking": "ROI Tracking",
    "common.readyToStart": "Ready to Experience It Yourself?",
    "common.readyDesc": "Start your journey with us today and see how easy project management can be",
    "common.getStartedNow": "Get Started Now",
    "common.goToPortal": "Go to My Portal",
  },
  fr: {
    // Navigation
    "nav.services": "Services",
    "nav.platform": "Plateforme",
    "nav.getStarted": "Commencer",
    "nav.signIn": "Se Connecter",
    "nav.myPortal": "Mon Portail",
    
    // Landing Page - Hero
    "landing.hero.partner": "Votre Partenaire Marketing",
    "landing.hero.title": "Agence Marketing Propulsée par l'IA au Maroc",
    "landing.hero.description": "Née à Martil, Maroc en 2019. Renée avec l'IA en 2025. Nous créons des solutions SaaS IA de pointe et des expériences marketing intelligentes qui allient créativité et technologie intelligente pour les marques mondiales.",
    "landing.hero.requestService": "Demander un Service",
    "landing.hero.viewRequests": "Voir Mes Demandes",
    
    // Stats
    "landing.stats.clients": "Clients Satisfaits",
    "landing.stats.projects": "Projets Complétés",
    "landing.stats.satisfaction": "Satisfaction Client",
    "landing.stats.support": "Support Disponible",
    
    // Features
    "landing.features.badge": "Notre Expertise",
    "landing.features.title": "Solutions Marketing Améliorées par l'IA",
    "landing.features.subtitle": "Combinant la créativité humaine avec l'intelligence artificielle pour des résultats exceptionnels",
    "landing.features.strategic": "Planification Stratégique",
    "landing.features.strategicDesc": "Stratégies basées sur les données adaptées à vos objectifs commerciaux",
    "landing.features.fast": "Exécution Rapide",
    "landing.features.fastDesc": "Exécution rapide sans compromettre la qualité",
    "landing.features.growth": "Axé sur la Croissance",
    "landing.features.growthDesc": "Méthodes éprouvées pour développer votre entreprise",
    "landing.features.support": "Support Dédié",
    "landing.features.supportDesc": "Une équipe engagée dans votre succès",
    
    // Platform
    "landing.platform.badge": "Notre Plateforme",
    "landing.platform.title": "Gestion de Projet Intelligente",
    "landing.platform.subtitle": "Découvrez une collaboration transparente avec notre plateforme propulsée par l'IA conçue pour les agences modernes",
    "landing.platform.dashboard": "Aperçu du Tableau de Bord",
    "landing.platform.dashboardDesc": "Obtenez une vue complète de tous vos projets actifs, activités récentes et indicateurs clés en un coup d'œil.",
    "landing.platform.projects": "Gestion de Projet",
    "landing.platform.projectsDesc": "Suivez l'avancement du projet, visualisez les jalons, surveillez les budgets et gérez tous les livrables en un seul endroit.",
    "landing.platform.requests": "Demandes de Service",
    "landing.platform.requestsDesc": "Visualisez et suivez toutes vos demandes de service avec des mises à jour de statut en temps réel et une communication directe.",
    "landing.platform.newRequest": "Soumettre de Nouvelles Demandes",
    "landing.platform.newRequestDesc": "Soumettez facilement de nouvelles demandes de service avec des descriptions détaillées et des notifications automatiques à notre équipe.",
    "landing.platform.invoices": "Factures et Paiements",
    "landing.platform.invoicesDesc": "Accédez à toutes vos factures, historique de paiement et détails de transaction avec des copies PDF téléchargeables.",
    "landing.platform.chat": "Communication Directe",
    "landing.platform.chatDesc": "Discutez directement avec notre équipe, recevez des notifications instantanées et obtenez de l'aide quand vous en avez besoin.",
    "landing.platform.analytics": "Analytiques et Aperçus",
    "landing.platform.analyticsDesc": "Surveillez la performance du projet, consultez des analyses détaillées et suivez votre ROI marketing avec des rapports complets.",
    
    // Form
    "landing.form.title": "Demander un Service",
    "landing.form.subtitle": "Remplissez le formulaire ci-dessous et notre équipe vous répondra dans les 24 heures",
    "landing.form.serviceType": "Type de Service",
    "landing.form.selectService": "Sélectionner un service",
    "landing.form.businessName": "Nom de l'Entreprise",
    "landing.form.contactName": "Nom du Contact",
    "landing.form.email": "Adresse Email",
    "landing.form.phone": "Numéro de Téléphone",
    "landing.form.description": "Description du Projet",
    "landing.form.descPlaceholder": "Parlez-nous de votre projet et de vos objectifs...",
    "landing.form.budget": "Fourchette Budgétaire",
    "landing.form.selectBudget": "Sélectionner la fourchette budgétaire",
    "landing.form.startDate": "Date de Début Préférée",
    "landing.form.submit": "Soumettre la Demande",
    "landing.form.submitting": "Envoi en cours...",
    
    // Common
    "common.quickSetup": "Configuration Rapide",
    "common.noCreditCard": "Aucune Carte de Crédit Requise",
    "common.realTimeUpdates": "Mises à Jour en Temps Réel",
    "common.fileManagement": "Gestion des Fichiers",
    "common.directMessaging": "Messagerie Directe",
    "common.meetingScheduler": "Planificateur de Réunions",
    "common.performanceMetrics": "Métriques de Performance",
    "common.roiTracking": "Suivi du ROI",
    "common.readyToStart": "Prêt à l'Essayer Vous-Même?",
    "common.readyDesc": "Commencez votre voyage avec nous aujourd'hui et voyez à quel point la gestion de projet peut être facile",
    "common.getStartedNow": "Commencer Maintenant",
    "common.goToPortal": "Aller à Mon Portail",
  },
  ar: {
    // Navigation
    "nav.services": "الخدمات",
    "nav.platform": "المنصة",
    "nav.getStarted": "ابدأ الآن",
    "nav.signIn": "تسجيل الدخول",
    "nav.myPortal": "بوابتي",
    
    // Landing Page - Hero
    "landing.hero.partner": "شريكك التسويقي",
    "landing.hero.title": "وكالة تسويق مدعومة بالذكاء الاصطناعي في المغرب",
    "landing.hero.description": "ولدت في مارتيل، المغرب في 2019. ولدت من جديد بالذكاء الاصطناعي في 2025. نصنع حلول SaaS AI متطورة وتجارب تسويقية ذكية تمزج الإبداع مع التكنولوجيا الذكية للعلامات التجارية العالمية.",
    "landing.hero.requestService": "طلب خدمة",
    "landing.hero.viewRequests": "عرض طلباتي",
    
    // Stats
    "landing.stats.clients": "عملاء سعداء",
    "landing.stats.projects": "مشاريع مكتملة",
    "landing.stats.satisfaction": "رضا العملاء",
    "landing.stats.support": "الدعم متاح",
    
    // Features
    "landing.features.badge": "خبرتنا",
    "landing.features.title": "حلول تسويقية محسنة بالذكاء الاصطناعي",
    "landing.features.subtitle": "دمج الإبداع البشري مع الذكاء الاصطناعي لنتائج استثنائية",
    "landing.features.strategic": "التخطيط الاستراتيجي",
    "landing.features.strategicDesc": "استراتيجيات قائمة على البيانات مصممة خصيصاً لأهداف عملك",
    "landing.features.fast": "تنفيذ سريع",
    "landing.features.fastDesc": "تنفيذ سريع دون المساس بالجودة",
    "landing.features.growth": "التركيز على النمو",
    "landing.features.growthDesc": "طرق مثبتة لتوسيع نطاق عملك",
    "landing.features.support": "دعم مخصص",
    "landing.features.supportDesc": "فريق ملتزم بنجاحك",
    
    // Platform
    "landing.platform.badge": "منصتنا",
    "landing.platform.title": "إدارة مشاريع ذكية",
    "landing.platform.subtitle": "اختبر التعاون السلس مع منصتنا المدعومة بالذكاء الاصطناعي المصممة للوكالات الحديثة",
    "landing.platform.dashboard": "نظرة عامة على لوحة التحكم",
    "landing.platform.dashboardDesc": "احصل على عرض شامل لجميع مشاريعك النشطة والأنشطة الأخيرة والمقاييس الرئيسية في لمحة واحدة.",
    "landing.platform.projects": "إدارة المشاريع",
    "landing.platform.projectsDesc": "تتبع تقدم المشروع، وعرض المعالم، ومراقبة الميزانيات، وإدارة جميع التسليمات في مكان واحد.",
    "landing.platform.requests": "طلبات الخدمة",
    "landing.platform.requestsDesc": "عرض وتتبع جميع طلبات الخدمة الخاصة بك مع تحديثات الحالة في الوقت الفعلي والتواصل المباشر.",
    "landing.platform.newRequest": "تقديم طلبات جديدة",
    "landing.platform.newRequestDesc": "قدم طلبات خدمة جديدة بسهولة مع أوصاف مفصلة وإشعارات تلقائية لفريقنا.",
    "landing.platform.invoices": "الفواتير والمدفوعات",
    "landing.platform.invoicesDesc": "الوصول إلى جميع فواتيرك، وسجل الدفع، وتفاصيل المعاملات مع نسخ PDF قابلة للتنزيل.",
    "landing.platform.chat": "التواصل المباشر",
    "landing.platform.chatDesc": "تحدث مباشرة مع فريقنا، واستقبل إشعارات فورية، واحصل على الدعم كلما احتجت إليه.",
    "landing.platform.analytics": "التحليلات والرؤى",
    "landing.platform.analyticsDesc": "راقب أداء المشروع، واستعرض التحليلات التفصيلية، وتتبع عائد الاستثمار التسويقي الخاص بك مع تقارير شاملة.",
    
    // Form
    "landing.form.title": "طلب خدمة",
    "landing.form.subtitle": "املأ النموذج أدناه وسيتواصل فريقنا معك خلال 24 ساعة",
    "landing.form.serviceType": "نوع الخدمة",
    "landing.form.selectService": "اختر الخدمة",
    "landing.form.businessName": "اسم الشركة",
    "landing.form.contactName": "اسم جهة الاتصال",
    "landing.form.email": "عنوان البريد الإلكتروني",
    "landing.form.phone": "رقم الهاتف",
    "landing.form.description": "وصف المشروع",
    "landing.form.descPlaceholder": "أخبرنا عن مشروعك وأهدافك...",
    "landing.form.budget": "نطاق الميزانية",
    "landing.form.selectBudget": "اختر نطاق الميزانية",
    "landing.form.startDate": "تاريخ البدء المفضل",
    "landing.form.submit": "إرسال الطلب",
    "landing.form.submitting": "جاري الإرسال...",
    
    // Common
    "common.quickSetup": "إعداد سريع",
    "common.noCreditCard": "لا حاجة لبطاقة ائتمان",
    "common.realTimeUpdates": "تحديثات في الوقت الفعلي",
    "common.fileManagement": "إدارة الملفات",
    "common.directMessaging": "المراسلة المباشرة",
    "common.meetingScheduler": "مجدول الاجتماعات",
    "common.performanceMetrics": "مقاييس الأداء",
    "common.roiTracking": "تتبع عائد الاستثمار",
    "common.readyToStart": "هل أنت مستعد لتجربته بنفسك؟",
    "common.readyDesc": "ابدأ رحلتك معنا اليوم وشاهد مدى سهولة إدارة المشاريع",
    "common.getStartedNow": "ابدأ الآن",
    "common.goToPortal": "انتقل إلى بوابتي",
  },
  it: {
    // Navigation
    "nav.services": "Servizi",
    "nav.platform": "Piattaforma",
    "nav.getStarted": "Inizia",
    "nav.signIn": "Accedi",
    "nav.myPortal": "Il Mio Portale",
    
    // Landing Page - Hero
    "landing.hero.partner": "Il Tuo Partner di Marketing",
    "landing.hero.title": "Agenzia di Marketing Potenziata dall'IA in Marocco",
    "landing.hero.description": "Nata a Martil, Marocco nel 2019. Rinata con l'IA nel 2025. Creiamo soluzioni SaaS AI all'avanguardia ed esperienze di marketing intelligenti che fondono creatività con tecnologia intelligente per marchi globali.",
    "landing.hero.requestService": "Richiedi Servizio",
    "landing.hero.viewRequests": "Vedi le Mie Richieste",
    
    // Stats
    "landing.stats.clients": "Clienti Felici",
    "landing.stats.projects": "Progetti Completati",
    "landing.stats.satisfaction": "Soddisfazione del Cliente",
    "landing.stats.support": "Supporto Disponibile",
    
    // Features
    "landing.features.badge": "La Nostra Esperienza",
    "landing.features.title": "Soluzioni di Marketing Potenziate dall'IA",
    "landing.features.subtitle": "Combinando la creatività umana con l'intelligenza artificiale per risultati eccezionali",
    "landing.features.strategic": "Pianificazione Strategica",
    "landing.features.strategicDesc": "Strategie basate sui dati su misura per i tuoi obiettivi aziendali",
    "landing.features.fast": "Esecuzione Rapida",
    "landing.features.fastDesc": "Esecuzione veloce senza compromettere la qualità",
    "landing.features.growth": "Focalizzato sulla Crescita",
    "landing.features.growthDesc": "Metodi comprovati per far crescere la tua attività",
    "landing.features.support": "Supporto Dedicato",
    "landing.features.supportDesc": "Un team impegnato nel tuo successo",
    
    // Platform
    "landing.platform.badge": "La Nostra Piattaforma",
    "landing.platform.title": "Gestione Progetti Intelligente",
    "landing.platform.subtitle": "Sperimenta una collaborazione senza soluzione di continuità con la nostra piattaforma potenziata dall'IA progettata per agenzie moderne",
    "landing.platform.dashboard": "Panoramica Dashboard",
    "landing.platform.dashboardDesc": "Ottieni una vista completa di tutti i tuoi progetti attivi, attività recenti e metriche chiave a colpo d'occhio.",
    "landing.platform.projects": "Gestione Progetti",
    "landing.platform.projectsDesc": "Traccia i progressi del progetto, visualizza le tappe, monitora i budget e gestisci tutti i deliverable in un unico posto.",
    "landing.platform.requests": "Richieste di Servizio",
    "landing.platform.requestsDesc": "Visualizza e traccia tutte le tue richieste di servizio con aggiornamenti di stato in tempo reale e comunicazione diretta.",
    "landing.platform.newRequest": "Invia Nuove Richieste",
    "landing.platform.newRequestDesc": "Invia facilmente nuove richieste di servizio con descrizioni dettagliate e notifiche automatiche al nostro team.",
    "landing.platform.invoices": "Fatture e Pagamenti",
    "landing.platform.invoicesDesc": "Accedi a tutte le tue fatture, cronologia dei pagamenti e dettagli delle transazioni con copie PDF scaricabili.",
    "landing.platform.chat": "Comunicazione Diretta",
    "landing.platform.chatDesc": "Chatta direttamente con il nostro team, ricevi notifiche istantanee e ottieni supporto quando ne hai bisogno.",
    "landing.platform.analytics": "Analisi e Approfondimenti",
    "landing.platform.analyticsDesc": "Monitora le prestazioni del progetto, rivedi analisi dettagliate e traccia il tuo ROI di marketing con report completi.",
    
    // Form
    "landing.form.title": "Richiedi un Servizio",
    "landing.form.subtitle": "Compila il modulo qui sotto e il nostro team ti risponderà entro 24 ore",
    "landing.form.serviceType": "Tipo di Servizio",
    "landing.form.selectService": "Seleziona un servizio",
    "landing.form.businessName": "Nome Azienda",
    "landing.form.contactName": "Nome Contatto",
    "landing.form.email": "Indirizzo Email",
    "landing.form.phone": "Numero di Telefono",
    "landing.form.description": "Descrizione Progetto",
    "landing.form.descPlaceholder": "Parlaci del tuo progetto e dei tuoi obiettivi...",
    "landing.form.budget": "Fascia di Budget",
    "landing.form.selectBudget": "Seleziona fascia di budget",
    "landing.form.startDate": "Data di Inizio Preferita",
    "landing.form.submit": "Invia Richiesta",
    "landing.form.submitting": "Invio in corso...",
    
    // Common
    "common.quickSetup": "Configurazione Rapida",
    "common.noCreditCard": "Nessuna Carta di Credito Richiesta",
    "common.realTimeUpdates": "Aggiornamenti in Tempo Reale",
    "common.fileManagement": "Gestione File",
    "common.directMessaging": "Messaggistica Diretta",
    "common.meetingScheduler": "Pianificatore Riunioni",
    "common.performanceMetrics": "Metriche di Prestazione",
    "common.roiTracking": "Tracciamento ROI",
    "common.readyToStart": "Pronto a Provarlo Tu Stesso?",
    "common.readyDesc": "Inizia il tuo viaggio con noi oggi e scopri quanto può essere facile la gestione dei progetti",
    "common.getStartedNow": "Inizia Ora",
    "common.goToPortal": "Vai al Mio Portale",
  }
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>("en");

  useEffect(() => {
    const loadLanguage = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase
          .from("profiles")
          .select("language_preference")
          .eq("id", user.id)
          .maybeSingle();
        
        if (data?.language_preference) {
          setLanguageState(data.language_preference as Language);
        }
      }
    };
    loadLanguage();
  }, []);

  const setLanguage = async (newLanguage: Language) => {
    setLanguageState(newLanguage);
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase
        .from("profiles")
        .update({ language_preference: newLanguage })
        .eq("id", user.id);
    }
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};